import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const ReportParameters = () => {
  const navigate = useNavigate();
  const [proceduresList, setProceduresList] = useState([]);
  const [accountsList, setAccountsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accountsError, setAccountsError] = useState(null);

  // User Type & LabId states
  const [labId, setLabId] = useState(null);
  const [userType, setUserType] = useState(null);
  const [authLoaded, setAuthLoaded] = useState(false);

  const getFirstDayOfMonth = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-01`;
  };

  const getLastDayOfMonth = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const lastDay = new Date(year, today.getMonth() + 1, 0).getDate();
    return `${year}-${month}-${lastDay}`;
  };

  const [formData, setFormData] = useState({
    creationStartDate: getFirstDayOfMonth(),
    creationEndDate: getLastDayOfMonth(),
    groupBy: '',
    estimatedValue: 'Whole Case',
    procedures: '',
    format: 'On Screen',
  });

  // ============================================================
  // Load LabId & UserType from localStorage FIRST - only once
  // ============================================================
  useEffect(() => {
    let storedLabId = localStorage.getItem('userLabId');
    let storedUserType = localStorage.getItem('userType');

    if (!storedLabId) {
      storedLabId = sessionStorage.getItem('userLabId');
    }

    if (!storedUserType) {
      storedUserType = sessionStorage.getItem('userType');
    }

    const userDataStr =
      localStorage.getItem('userData') || sessionStorage.getItem('userData');

    if (userDataStr) {
      try {
        const userData = JSON.parse(userDataStr);
        if (!storedLabId && userData.labId) storedLabId = userData.labId;
        if (!storedUserType && userData.userType) storedUserType = userData.userType;
      } catch (e) {
        // ignore
      }
    }

    if (storedLabId) setLabId(storedLabId);

    if (storedUserType) {
      setUserType(storedUserType);
    } else if (storedLabId) {
      setUserType('lab');
    } else {
      setUserType('admin');
    }

    setAuthLoaded(true);
  }, []);

  // ============================================================
  // Fetch Procedures — lab-wise (same logic as AddFile.js)
  // ============================================================
  useEffect(() => {
    if (!authLoaded) return;

    const fetchProcedures = async () => {
      if (!userType) return;
      if (userType === 'lab' && !labId) return;

      try {
        setLoading(true);
        setError(null);

        let proceduresData = [];

        if (userType === 'lab' && labId) {
          // ---- Lab mode: try lab-specific, fallback to all ----
          try {
            const response = await axios.get(
              `https://hms.backend.hiragroupcloud.com/api/procedures/lab-procedures/${labId}`
            );

            if (response.data.success && response.data.data) {
              proceduresData = response.data.data;
            } else {
              // fallback to all procedures
              const allRes = await axios.get(
                'https://hms.backend.hiragroupcloud.com/api/procedures/procedures'
              );
              if (allRes.data.success && allRes.data.data) {
                proceduresData = allRes.data.data;
              } else if (Array.isArray(allRes.data)) {
                proceduresData = allRes.data;
              }
            }
          } catch (labErr) {
            console.error('Lab procedures fetch failed, trying all:', labErr);
            try {
              const allRes = await axios.get(
                'https://hms.backend.hiragroupcloud.com/api/procedures/procedures'
              );
              if (allRes.data.success && allRes.data.data) {
                proceduresData = allRes.data.data;
              } else if (Array.isArray(allRes.data)) {
                proceduresData = allRes.data;
              }
            } catch (fallbackErr) {
              console.error('Fallback procedures fetch failed:', fallbackErr);
              throw fallbackErr;
            }
          }
        } else {
          // ---- Admin mode: all procedures ----
          try {
            const response = await axios.get(
              'https://hms.backend.hiragroupcloud.com/api/procedures/procedures'
            );
            if (response.data.success && response.data.data) {
              proceduresData = response.data.data;
            } else if (Array.isArray(response.data)) {
              proceduresData = response.data;
            }
          } catch (adminErr) {
            // some APIs use /api/procedures
            const response = await axios.get('https://hms.backend.hiragroupcloud.com/api/procedures');
            if (response.data.success && response.data.data) {
              proceduresData = response.data.data;
            } else if (Array.isArray(response.data)) {
              proceduresData = response.data;
            }
          }
        }

        // ---- Format: accept procedure_name OR name; procedure_code OR procedureId ----
        const formattedProcedures = proceduresData
          .filter((proc) => proc && (proc.procedure_name || proc.name))
          .map((proc) => ({
            _id: proc._id || proc.procedureId || proc.id,
            name: (proc.procedure_name || proc.name || '').trim(),
            procedureId: proc.procedure_code || proc.procedureId || proc.code || '',
          }));

        // Optional: remove duplicates by name
        const seen = new Set();
        const uniqueProcedures = formattedProcedures.filter((p) => {
          const key = `${p.name}|${p.procedureId}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        setProceduresList(uniqueProcedures);
      } catch (err) {
        console.error('Error fetching procedures:', err);
        setError(err.message || 'Failed to load procedures');
      } finally {
        setLoading(false);
      }
    };

    fetchProcedures();
  }, [userType, labId, authLoaded]);

  // ============================================================
  // Fetch Accounts
  // ============================================================
  useEffect(() => {
    if (!authLoaded) return;

    const fetchAccounts = async () => {
      if (!userType) return;
      if (userType === 'lab' && !labId) return;

      try {
        setAccountsLoading(true);
        setAccountsError(null);

        let accounts = [];

        if (userType === 'lab' && labId) {
          const response = await axios.get(
            `https://hms.backend.hiragroupcloud.com/api/account/lab-accounts/${labId}`
          );

          if (response.data.success) {
            accounts = response.data.data || [];
          } else {
            throw new Error(response.data.message || 'Failed to fetch lab accounts');
          }
        } else {
          const [hmsResponse, ddxResponse] = await Promise.all([
            axios.get('https://hms.backend.hiragroupcloud.com/api/account/account'),
            axios.get('https://hms.backend.hiragroupcloud.com/api/users'),
          ]);

          const hmsAccounts = (hmsResponse.data.data || []).map((acc) => ({
            ...acc,
            account_name: acc.account_name || 'Unnamed',
            source: 'HMS',
          }));

          const ddxAccounts = (ddxResponse.data.users || []).map((user) => ({
            _id: user._id,
            account_name:
              user.practiceName || `${user.firstName} ${user.lastName}`,
            source: 'DDX',
          }));

          accounts = [...hmsAccounts, ...ddxAccounts];
        }

        const filteredAccounts = accounts
          .filter((acc) => acc && acc.account_name)
          .map((acc) => ({
            _id: acc._id || acc.accountId || acc.id,
            account_name: acc.account_name.trim(),
          }));

        setAccountsList(filteredAccounts);
      } catch (err) {
        setAccountsError(err.message || 'Failed to load accounts');
      } finally {
        setAccountsLoading(false);
      }
    };

    fetchAccounts();
  }, [userType, labId, authLoaded]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRun = async () => {
    if (formData.format === 'Excel') {
      try {
        const response = await axios.get('https://hms.backend.hiragroupcloud.com/api/cases/cases');
        const allCases = response.data;

        const filtered = allCases.filter((c) => {
          const createdAt = new Date(c.createdAt);
          const start = new Date(formData.creationStartDate);
          const end = new Date(formData.creationEndDate);
          end.setHours(23, 59, 59, 999);

          if (createdAt < start || createdAt > end) return false;
          if (formData.groupBy && c.account_name !== formData.groupBy) return false;
          if (
            formData.procedures &&
            !c.procedures.some((p) => p.procedure === formData.procedures)
          )
            return false;

          return true;
        });

        const headers = [
          'Account',
          'Case ID',
          'Patient',
          'Estimated Value',
          'Tags',
          'On-Hold',
          'Procedure(s)',
          'Units',
        ];
        const rows = filtered.map((c) => [
          c.account_name || 'Unknown Account',
          c.case_id,
          `${c.first_name} ${c.last_name}`,
          `£${(c.totalPrice || 0).toFixed(2)}`,
          c.tags?.join(', ') || '',
          c.onHoldReason ? 'Yes' : '',
          c.procedures.map((p) => p.procedure).join(', '),
          c.procedures.length,
        ]);

        const csvContent = [
          headers.join(','),
          ...rows.map((row) => row.map((item) => `"${item}"`).join(',')),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', 'report_cases.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (err) {
        alert('Error generating CSV report. Please try again.');
      }
    } else {
      navigate('/report-result', { state: { formData } });
    }
  };

  if (!authLoaded) {
    return (
      <div style={styles.loadingContainer}>
        <h2 style={styles.reportHeader}>Parameters</h2>
        <div style={styles.reportContainer}>
          <div style={styles.loadingState}>
            <p>Loading user information...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <h2 style={styles.reportHeader}>Parameters</h2>
      <div style={styles.reportContainer}>

        <div style={styles.formGroup}>
          <label htmlFor="creationStartDate" style={styles.formLabel}>
            Creation Start Date<span style={styles.required}>*</span>
          </label>
          <input
            type="date"
            id="creationStartDate"
            name="creationStartDate"
            value={formData.creationStartDate}
            onChange={handleChange}
            style={styles.formInput}
          />
        </div>

        <div style={styles.formGroup}>
          <label htmlFor="creationEndDate" style={styles.formLabel}>
            Creation End Date<span style={styles.required}>*</span>
          </label>
          <input
            type="date"
            id="creationEndDate"
            name="creationEndDate"
            value={formData.creationEndDate}
            onChange={handleChange}
            style={styles.formInput}
          />
        </div>

        <div style={styles.formGroup}>
          <label htmlFor="groupBy" style={styles.formLabel}>Account</label>
          {accountsLoading ? (
            <div style={styles.loadingState}>
              <p style={styles.loadingText}>Loading accounts...</p>
            </div>
          ) : accountsError ? (
            <div style={styles.errorState}>
              <p style={styles.errorText}>Error: {accountsError}</p>
            </div>
          ) : (
            <select
              id="groupBy"
              name="groupBy"
              value={formData.groupBy}
              onChange={handleChange}
              style={styles.formSelect}
            >
              <option value="">All Accounts</option>
              {accountsList.map((account) => (
                <option key={account._id} value={account.account_name}>
                  {account.account_name}
                </option>
              ))}
            </select>
          )}
        </div>

        <div style={styles.formGroup}>
          <label htmlFor="procedures" style={styles.formLabel}>Procedures</label>
          {loading ? (
            <div style={styles.loadingState}>
              <p style={styles.loadingText}>Loading procedures...</p>
            </div>
          ) : error ? (
            <div style={styles.errorState}>
              <p style={styles.errorText}>Error: {error}</p>
            </div>
          ) : (
            <select
              id="procedures"
              name="procedures"
              value={formData.procedures}
              onChange={handleChange}
              style={styles.formSelect}
              size={Math.min(proceduresList.length + 1, 10)}
            >
              <option value="">All Procedures</option>
              {proceduresList.map((procedure) => (
                <option key={procedure._id} value={procedure.name}>
                  {procedure.name}
                  {procedure.procedureId ? ` (${procedure.procedureId})` : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        <div style={styles.formGroup}>
          <label htmlFor="format" style={styles.formLabel}>
            Format<span style={styles.required}>*</span>
          </label>
          <select
            id="format"
            name="format"
            value={formData.format}
            onChange={handleChange}
            style={styles.formSelect}
          >
            <option value="On Screen">On Screen</option>
            <option value="Excel">CSV(Excel)</option>
          </select>
        </div>

        <div style={styles.formActions}>
          <button
            type="button"
            onClick={handleRun}
            style={styles.runButton}
            disabled={loading || accountsLoading}
          >
            {loading || accountsLoading ? 'Loading...' : 'Run'}
          </button>
        </div>
      </div>
    </>
  );
};

const styles = {
  reportHeader: {
    color: '#004aad',
    fontSize: '20px',
    fontWeight: 'normal',
    paddingLeft: '20px',
    marginBottom: '10px',
    marginLeft: '-20px',
  },
  reportContainer: {
    backgroundColor: '#f7f7f7',
    border: '1px solid #ddd',
    borderTop: '3px solid #004aad',
    padding: '20px',
    borderRadius: '0 0 8px 8px',
    fontFamily: 'Arial, sans-serif',
    maxWidth: '600px',
    marginLeft: '-1px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  formGroup: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '15px',
    minHeight: '40px',
  },
  formLabel: {
    flex: '0 0 150px',
    fontWeight: 'bold',
    color: '#555',
    fontSize: '14px',
  },
  required: { color: 'red', marginLeft: '4px' },
  formInput: {
    flex: 1,
    padding: '8px 12px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: '#fff',
  },
  formSelect: {
    flex: 1,
    padding: '8px 12px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: '#fff',
    minHeight: '36px',
  },
  loadingContainer: {
    padding: '20px',
  },
  loadingState: {
    flex: 1,
    padding: '8px 0',
    textAlign: 'center',
  },
  errorState: {
    flex: 1,
    padding: '8px 0',
  },
  loadingText: {
    color: '#666',
    fontStyle: 'italic',
    margin: 0,
    fontSize: '14px',
  },
  errorText: {
    color: '#d32f2f',
    margin: 0,
    fontSize: '14px',
  },
  formActions: {
    textAlign: 'right',
    marginTop: '20px',
  },
  runButton: {
    backgroundColor: '#004aad',
    color: 'white',
    padding: '10px 20px',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'background-color 0.3s',
    minWidth: '100px',
  },
};

export default ReportParameters;